import Logo from '@/assets/images/adaptee-gastro-logo.svg'
import CompactHeaderBackButton from '@/components/MainHeader/CompactMainHeaderBackButton'
import CompactHeaderMenuButton from '@/components/MainHeader/CompactMainHeaderMenuButton'
import ExtendedHeaderTime from '@/components/MainHeader/ExtendedMainHeaderTime'
import { useCurrentMainHeaderOverrides } from '@/components/MainHeader/contexts/Overrides'
import { ColorEnum } from '@/constants/color'
import { useRouterCanGoBackParam } from '@/features/navigation/hooks/can-go-back'
import NotificationFactory from '@/features/notifications/components/HeaderNotification/Factory'
import { useClearHeaderNotificationOnPushPressAction } from '@/features/notifications/components/HeaderNotification/hooks/useClearHeaderNotificationOnPushPressAction'
import { useHeaderNotification } from '@/features/notifications/components/HeaderNotification/hooks/useHeaderNotification'
import NotificationCenter from '@/features/notifications/components/NotificationCenter'
import { useNotificationCenterToggle } from '@/features/notifications/components/NotificationCenter/hooks/useNotificationCenterToggle'
import { useScreenLayout } from '@/hooks/screen'
import { Header as RNEHeader } from '@react-navigation/elements'
import { FC } from 'react'
import { Text, View } from 'react-native'
import { createStyleSheet, useStyles } from 'react-native-unistyles'

interface MainHeaderProps {
    routeTitle?: string
}

const MainHeader: FC<MainHeaderProps> = ({ routeTitle }) => {
    const [isOpened, toggle] = useNotificationCenterToggle()

    const screenLayout = useScreenLayout()
    const headerOverrides = useCurrentMainHeaderOverrides()

    const isCompactLayoutNotificationCenterOpened = screenLayout === 'compact' && isOpened

    const [notification, clear] = useHeaderNotification(isCompactLayoutNotificationCenterOpened)

    useClearHeaderNotificationOnPushPressAction(notification?.uuid, clear)

    const canGoBack = useRouterCanGoBackParam()
    const isCompactBackHeader = screenLayout === 'compact' && canGoBack

    const { styles } = useStyles(stylesheet, { isNotification: !!notification })

    const renderHeaderTitle = () => {
        if (notification && !isCompactLayoutNotificationCenterOpened) {
            return (
                <NotificationFactory
                    key={notification.uuid}
                    notification={notification}
                    onClose={clear}
                />
            )
        }

        if (headerOverrides?.title !== undefined) {
            return <Text style={styles.headerTitle}>{headerOverrides.title}</Text>
        }

        if (isCompactBackHeader && routeTitle)
            return <Text style={styles.headerTitle}>{routeTitle}</Text>

        return <Logo height={32} width={90} />
    }

    const hideHeaderSideComponents = notification && screenLayout === 'compact' && !isOpened

    const renderHeaderLeft = () => {
        if (headerOverrides?.left !== undefined) return headerOverrides.left

        if (hideHeaderSideComponents) return

        if (screenLayout === 'extended') return <ExtendedHeaderTime />

        if (canGoBack) return <CompactHeaderBackButton />

        return <CompactHeaderMenuButton />
    }

    const renderHeaderRight = () => {
        if (headerOverrides?.right !== undefined) return headerOverrides.right

        if (hideHeaderSideComponents) return
        if (isCompactBackHeader) return

        return (
            <View style={styles.headerRightContainer}>
                <NotificationCenter
                    opened={isOpened}
                    toggle={toggle}
                    toggleButtonStyle={styles.notificationCenterToggleButton}
                />
            </View>
        )
    }

    return (
        <RNEHeader
            title=""
            headerTitle={renderHeaderTitle}
            headerTitleAlign="center"
            headerTitleContainerStyle={styles.headerTitleContainer}
            headerLeft={renderHeaderLeft}
            headerRight={renderHeaderRight}
            headerStyle={styles.header}
        />
    )
}

const stylesheet = createStyleSheet((_, { insets }) => ({
    notificationCenterToggleButton: {
        marginRight: 16,
    },
    headerTitleContainer: {
        variants: {
            isNotification: {
                true: {
                    alignItems: 'center',
                    width: '100%',
                },
            },
        },
    },
    header: {
        backgroundColor: ColorEnum.BRICK_700,
        borderBottomWidth: 0,
        shadowOpacity: 0,
        height: insets.top + 56,
    },
    headerRightContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 24,
    },
    headerTitle: {
        color: ColorEnum.WHITE,
        fontSize: 14,
        fontWeight: '700',
        textTransform: 'uppercase',
        letterSpacing: 0.7,
    },
}))

export default MainHeader
